// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState, type ReactNode } from 'react';

import type { CustomProperty, CustomPropertyId, CustomPropertyTypeName } from '@evolvemep/api-ts';
import { DragDropContext, Draggable, Droppable, type OnDragEndResponder } from '@hello-pangea/dnd';
import { ActionIcon, Card, Divider, Flex, OptionalPortal, Stack, Text, TextInput } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AddEditCustomPropertyPopover } from 'components/Mantine/CustomProperties/AddEditCustomPropertyPopover';
import { dataTypeIconMap, dataTypeLabelMap } from 'components/Mantine/CustomProperties/constants';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { GridMenu } from 'components/Mantine/GridMenu';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { isNil, isNotNil } from 'helpers/isNotNil';

type Props = {
  customPropertyTypeName: CustomPropertyTypeName;
  topRightSection?: ReactNode;
};

export const CustomPropertiesContents = ({ customPropertyTypeName, topRightSection }: Props) => {
  const { customProperties, deleteProperty, reorderCustomProperty, loading } =
    useCustomProperties(customPropertyTypeName);
  const [searchPhrase, setSearchPhrase] = useState('');

  const [deletingProperty, setDeletingProperty] = useState<CustomProperty | null>(null);

  const [reordering, setReordering] = useState<CustomPropertyId | null>(null);
  const onDragEnd: OnDragEndResponder = ({ source, destination, draggableId }) => {
    if (isNil(destination) || destination.index === source.index || isNil(customProperties)) return;

    const after =
      customProperties[source.index > destination.index ? destination.index - 1 : destination.index]?.customPropertyId;

    const customPropertyId = draggableId as CustomPropertyId;
    setReordering(customPropertyId);
    void reorderCustomProperty(customPropertyId, after).finally(() => setReordering(null));
  };

  const searchLower = searchPhrase.toLowerCase();
  const searchMatchingCustomProperties = useMemo(
    () => customProperties?.filter((p) => p.name.toLowerCase().includes(searchLower)),
    [customProperties, searchLower],
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Flex justify="space-between" align="center" gap="md" wrap="wrap-reverse">
        <TextInput
          placeholder="Search..."
          flex={1}
          value={searchPhrase}
          onChange={(e) => setSearchPhrase(e.target.value)}
          miw={200}
          maw={300}
        />
        <Flex flex="1 0 auto" justify="flex-end">
          {topRightSection}
        </Flex>
      </Flex>
      <Droppable droppableId="custom-properties">
        {(provided) => (
          <Card mih={70} p={0} ref={provided.innerRef} {...provided.droppableProps}>
            {searchMatchingCustomProperties?.length === 0 && !loading && (
              <Flex flex={1} align="center" justify="center">
                <Text fz="sm" c="dimmed">
                  {customProperties?.length === 0
                    ? 'Add a new property to get started.'
                    : 'No matching properties found.'}
                </Text>
              </Flex>
            )}
            {searchMatchingCustomProperties?.map((p, i, arr) => (
              <Draggable
                key={p.customPropertyId}
                index={i}
                draggableId={p.customPropertyId}
                isDragDisabled={isNotNil(reordering)}
              >
                {(draggableProvided, snapshot) => (
                  <OptionalPortal withinPortal={snapshot.isDragging}>
                    <Card
                      px="md"
                      py="xs"
                      mih={70}
                      ref={draggableProvided.innerRef}
                      shadow={snapshot.isDragging ? 'lg' : undefined}
                      {...draggableProvided.draggableProps}
                    >
                      <Flex gap="xs" align="center" justify="space-between" flex={1}>
                        <Flex gap="xs" align="center">
                          <AgGridStyleTooltip label="Drag to reorder" withArrow hidden={snapshot.isDragging}>
                            <ActionIcon
                              variant="white"
                              loading={reordering === p.customPropertyId}
                              disabled={isNotNil(reordering)}
                              style={{ cursor: 'grab' }}
                              {...draggableProvided.dragHandleProps}
                            >
                              <EvolveIcon icon="Draggable" color={snapshot.isDragging ? 'gray.9' : 'gray'} />
                            </ActionIcon>
                          </AgGridStyleTooltip>
                          <AgGridStyleTooltip label={dataTypeLabelMap[p.dataType]} withArrow>
                            <EvolveIcon {...dataTypeIconMap[p.dataType]} />
                          </AgGridStyleTooltip>
                          <Stack gap={0}>
                            <Text lineClamp={1} style={{ wordBreak: 'break-word' }}>
                              {p.name}
                            </Text>
                            <Text
                              c="dimmed"
                              fz="sm"
                              lineClamp={2}
                              style={{ wordBreak: 'break-word' }}
                              title={p.description}
                            >
                              {p.description}
                            </Text>
                          </Stack>
                        </Flex>
                        <GridMenu
                          options={['Edit', 'Delete']}
                          onMenuItemSelected={(menuItemSelected) => {
                            if (menuItemSelected === 'Delete') {
                              setDeletingProperty(p);
                              return true;
                            }
                            return false;
                          }}
                        >
                          {(menuItemSelected, resetSelection) => (
                            <AddEditCustomPropertyPopover
                              customPropertyTypeName={customPropertyTypeName}
                              customProperty={p}
                              opened={menuItemSelected === 'Edit'}
                              onClose={resetSelection}
                            />
                          )}
                        </GridMenu>
                      </Flex>
                    </Card>
                    {i < arr.length - 1 && !snapshot.isDragging && <Divider />}
                  </OptionalPortal>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Card>
        )}
      </Droppable>
      <VariableTrackingModal
        title="Delete property"
        onClose={() => setDeletingProperty(null)}
        variable={deletingProperty}
        onConfirm={async (property) => deleteProperty(property.customPropertyId)}
        confirmationText="Delete"
      >
        {(property) => (
          <>
            <Text>
              Are you sure you want to delete <b>{property.name}</b>?
            </Text>
            <Text>This will also remove the property from all {customPropertyTypeName}s.</Text>
          </>
        )}
      </VariableTrackingModal>
    </DragDropContext>
  );
};
